import { File } from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { PdfViewer } from 'chat/messages/file/pdf/view'
import { FileItemView } from 'chat/messages/file/view'
import {
  getFriendlyMimeType,
  isImageType,
  isPdfType,
  isVideoType,
} from 'file_folder_repository/util'
import { FileFolderIcon } from 'file_folder_repository/views/file_folder_icon'
import moment from 'moment'
import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { RoundedButton } from 'shared/components/rounded-button'

interface Props {
  file: File | undefined
  url: string | undefined
  mimeType: string | undefined
  onError(error: Error): void
  onOpenWebLink: () => void
  onOpenFile: () => void
  onGoBack(): void
}
export function FileViewerView({
  url,
  mimeType,
  file,
  onError,
  onOpenWebLink,
  onOpenFile,
  onGoBack,
}: Props) {
  const styles = useStyles()

  if (!file) {
    return null
  }
  if (url) {
    if (isPdfType(mimeType)) {
      return (
        <PdfViewer
          source={{ uri: url }}
          onError={onError}
          goBack={onGoBack}
          title={file.name}
        />
      )
    }

    if (mimeType && (isImageType(mimeType) || isVideoType(mimeType))) {
      return (
        <View style={styles.fileItemViewContainer}>
          <FileItemView item={{ uri: url, mimeType }} />
        </View>
      )
    }
  }
  return (
    <FileView
      file={file}
      onPressWebLink={onOpenWebLink}
      onPressFile={onOpenFile}
    />
  )
}

interface FileViewProps {
  file: File
  onPressWebLink: () => void
  onPressFile: () => void
}

const FileView = ({ file, onPressWebLink, onPressFile }: FileViewProps) => {
  const isExternalFile = file.currentVersion?.externalUrl
  const styles = useStyles()
  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <View style={styles.fileFolderIconContainer}>
          <FileFolderIcon
            resource={file}
            size={100}
            style={styles.fileFolderIcon}
            externalLinkImageContentFit="contain"
          />
        </View>
        <View style={styles.fileDetailsContainer}>
          <Text style={styles.text}>{`${t('name')} : ${file.name}`}</Text>
          <Text style={styles.text}>{`${t('description')} : ${
            file.description ?? t('none')
          }`}</Text>
          <Text style={styles.text}>
            {`${t('type')} : ${
              isExternalFile
                ? t('webDocument')
                : getFriendlyMimeType(file.currentVersion?.fileMimeType)
            }`}
          </Text>
          {isExternalFile ? (
            <Text style={styles.text}>
              {`URL : ${file.currentVersion?.externalUrl}`}
            </Text>
          ) : null}
          <Text style={styles.text}>
            {`${t('uploadedOn')} : ${
              file.created
                ? moment(file.created).format('MMM DD, YYYY, hh:mm A')
                : t('unknown')
            }`}
          </Text>
          <Text style={styles.text}>
            {`Uploaded By: ${file.currentVersion?.uploadedBy.firstName} ${file.currentVersion?.uploadedBy.lastName}`}
          </Text>
        </View>
        <RoundedButton
          onPress={() => (isExternalFile ? onPressWebLink() : onPressFile())}
          title={isExternalFile ? t('openWebLink') : t('openFile')}
          danger
          containerStyle={styles.buttonContainer}
        />
      </View>
    </SafeAreaView>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  fileDetailsContainer: {
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey11 : theme.colors.grey6,
  },
  text: {
    margin: 10,
  },
  container: { flex: 1 },
  fileFolderIcon: {
    height: 150,
    width: 300,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fileFolderIconContainer: {
    padding: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonContainer: { margin: 10 },
  fileItemViewContainer: { flex: 1, backgroundColor: 'black' },
}))
